import React, { useEffect, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SHARING_SUMMARY } from '../data/sharingPrinciples';
import SEOHead from '../components/seo/SEOHead';
import HeroCarousel from '../components/sections/HeroCarousel';
import ProjectExplorer from '../components/sections/ProjectExplorer';
import PresentationVideo from '../components/sections/PresentationVideo';
import IconBadge from '../components/ui/IconBadge';
import analytics from '../utils/analytics';
import { IconChartLine, IconUsers, IconLeaf, IconShieldAlt, IconArrowRight, IconTrophy, IconWallet, IconUserPlus, IconHandHoldingUsd, IconRocket, IconStar, IconMapMarkerAlt, IconPercent, IconCoins, IconBullseye, IconBookOpen, IconFileAlt, IconIdCard, IconUserTie, IconWhatsapp } from '../components/ui/icons';
import { SITE } from '../config/site';
import { useAuth } from '../auth/EnhancedSupabaseAuthContext';
import {
  usePlatformStats, formatCount, formatXofCompact, formatRate, formatReturnRange
} from '../services/platformStats';
import ImageWithFallback from '../components/shared/ImageWithFallback';
import { MIN_INVESTMENT_LABEL } from '../config/paymentMethods';
import { calculateProgressSafe } from '../utils/numberFormatting';

// Nouveaux imports pour les projets
import { useProjects } from '../context/ProjectContext';
import { NewProjectBanner, NewProjectOverlay } from '../components/ui/NewProjectBadge';

// Images parlantes pour enrichir l'expérience
import financeImage from '../assets/icones/finance.jpg';
import { SECTORS } from '../config/sectors';
import { VALUES } from '../data/values';
import { defaultProjects } from '../data/projects';
import SocialFollow from '../components/ui/SocialFollow';
import { CountUp } from '../components/ui/ScrollEffects';
import PartnerMarquee from '../components/ui/PartnerMarquee';
import { PARTNERS } from '../data/partners';

// Photos d'illustration prises au Niger (licence libre : crédits affichés sous « Notre impact en images »)
const PHOTOS = {
  energie: '/assets/media/secteurs/energie.jpg',
  marche: '/assets/media/projet-embouche-marche-betail.jpg',
  agriculture: '/assets/media/secteurs/agriculture.jpg',
  eau: '/assets/media/secteurs/eau.jpg',
  commerce: '/assets/media/secteurs/commerce.jpg',
  broyeur: '/assets/media/projet-aliment-betail-broyeur.jpg'
};
const PHOTO_SECTORS = ['Énergie', 'Agriculture et élevage', 'Eau et assainissement', 'Commerce'];

const PHOTO_PROJECTS = ['static-7', 'static-8'];

// Boutons de la page : même hiérarchie que le bandeau d'accueil
// (orange = action principale, contour = action secondaire)
const CTA_PRIMARY = 'inline-flex items-center justify-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl shadow-lg transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300';
const CTA_SECONDARY = 'inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-blue-700 text-blue-800 hover:bg-blue-50 font-bold rounded-xl transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300';

const Home = () => {
  const { user } = useAuth() || {};
  // Chiffres réels : projets publiés + paiements confirmés (src/services/platformStats.js)
  const stats = usePlatformStats();
  // Avant le premier paiement confirmé, pas de compteurs « 0 FCFA collectés » / « 0 investisseurs » :
  // ils sont remplacés par l'objectif de collecte et une invitation à investir parmi les premiers
  const launching = !(stats.totalRaised > 0) && !(stats.investorsCount > 0);
  const navigate = useNavigate();

  // Contexte des projets pour afficher les nouveaux avec protection
  const projectContext = useProjects();
  const projects = projectContext?.projects || [];
  const getNewProjectsCount = projectContext?.getNewProjectsCount;

  // Filtrer les projets récents (nouveaux)
  const recentProjects = projects.filter(project => project.isNew || project.isRecentlyAdded).slice(0, 3);
  const newProjectsCount = getNewProjectsCount ? getNewProjectsCount() : 0;

  useEffect(() => {
    // Tracker la visite de la page d'accueil
    analytics.trackPageView('home', {
      is_first_visit: !localStorage.getItem('hasVisited'),
      referrer: document.referrer,
    });

    // Marquer comme visité
    try {
      localStorage.setItem('hasVisited', 'true');
    } catch (error) {
      // Stockage indisponible (navigation privée) : sans conséquence
    }
  }, []);

  return (
    <div className='bg-white'>
      {/* SEO Head pour optimisation */}
      <SEOHead
        description={`Investissez dès ${MIN_INVESTMENT_LABEL} dans des projets d'entrepreneurs du Niger, payez par Mobile Money et suivez l'impact de votre argent. Rendements visés non garantis.`}
        keywords="investissement impact social niger finance durable développement économique afrique projet responsable microfinance agriculture"
        type="website"
      />

      {/* Hero Section Premium avec Carousel */}
      <HeroCarousel />

      {/* Vidéo de présentation commentée de la plateforme */}
      <PresentationVideo />

      {/* Explorateur de projets : recherche, filtres et vrais projets ouverts */}
      <ProjectExplorer />

      {/* Section Nouveaux Projets */}
      {newProjectsCount > 0 && (
        <section className="py-16 bg-gradient-to-r from-blue-50 to-emerald-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Bannière des nouveaux projets */}
            <NewProjectBanner
              count={newProjectsCount}
              onViewAll={() => navigate('/projects?filter=new')}
              className="mb-12"
            />

            {/* Grille des nouveaux projets */}
            {recentProjects.length > 0 && (
              <div className="grid md:grid-cols-3 gap-8">
                {recentProjects.map((project) => (
                  <NewProjectOverlay key={project.id} isNew={project.isNew}>
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      {/* Image du projet */}
                      <div className="relative h-48 overflow-hidden">
                        <ImageWithFallback
                          src={project.imageUrl || 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80'}
                          alt={project.titre}
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                            {project.category || 'Projet'}
                          </span>
                        </div>
                      </div>

                      {/* Contenu du projet */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2">
                          {project.titre}
                        </h3>
                        <p className="text-gray-600 mb-4 line-clamp-3">
                          {project.description}
                        </p>

                        {/* Métriques du projet */}
                        <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                          <span><IconCoins className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden="true" /> {new Intl.NumberFormat('fr-FR').format(project.coutTotal)} FCFA</span>
                          <span><IconMapMarkerAlt className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden="true" /> {project.location}</span>
                        </div>

                        {/* Barre de progression */}
                        <div className="mb-4">
                          <div className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>Progression</span>
                            <span>{Math.round(calculateProgressSafe(project.montantRecu, project.coutTotal))}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-emerald-500 to-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${calculateProgressSafe(project.montantRecu, project.coutTotal)}%` }}
                            />
                          </div>
                        </div>

                        {/* Bouton d'action */}
                        <Link
                          to={`/projects/${project.id}`}
                          className="w-full bg-gradient-to-r from-emerald-500 to-blue-600 text-white text-center py-2 rounded-lg hover:from-emerald-600 hover:to-blue-700 transition-all duration-300 font-medium flex items-center justify-center"
                        >
                          Découvrir le projet
                          <IconArrowRight className="ml-2 text-sm" />
                        </Link>
                      </div>
                    </div>
                  </NewProjectOverlay>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Vitrine des Projets avec images des réalisations */}
      <Suspense fallback={
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      }>
        <section className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block bg-green-100 text-green-800 text-sm font-semibold px-4 py-2 rounded-full mb-4">
                 Nos réalisations
              </span>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Où en sont nos projets
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Chiffres calculés automatiquement à partir des projets publiés et des paiements confirmés
              </p>
            </div>

            {/* Grid des réalisations avec images */}
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="group relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 hover:scale-105">
                <ImageWithFallback
                  src={PHOTOS.energie}
                  alt="Centrale solaire de Malbaza, au Niger"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  {launching ? (
                    <>
                      <h3 className="text-xl font-bold mb-2">{formatXofCompact(stats.totalTarget)} recherchés</h3>
                      <p className="text-blue-200">pour {stats.openProjects} projets ouverts au Niger</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold mb-2">{formatXofCompact(stats.totalRaised)} collectés</h3>
                      <p className="text-blue-200">{stats.fundingRate} % de l'objectif total ({formatXofCompact(stats.totalTarget)})</p>
                    </>
                  )}
                </div>
              </div>

              <div className="group relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 hover:scale-105">
                <ImageWithFallback
                  src={PHOTOS.marche}
                  alt="Marché à bétail de N'Gonga, au Niger"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  {launching ? (
                    <>
                      <h3 className="text-xl font-bold mb-2">Soyez parmi les premiers investisseurs</h3>
                      <p className="text-blue-200">dès {MIN_INVESTMENT_LABEL}, par Mobile Money</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-xl font-bold mb-2">{formatCount(stats.investorsCount)} investisseurs</h3>
                      <p className="text-blue-200">engagés dans {stats.projectsCount} projets au Niger</p>
                    </>
                  )}
                </div>
              </div>

              <div className="group relative overflow-hidden rounded-2xl shadow-xl transition-all duration-300 hover:scale-105">
                <ImageWithFallback
                  src={PHOTOS.agriculture}
                  alt="Maraîchage au Niger"
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-xl font-bold mb-2">{formatCount(stats.beneficiariesTarget)} bénéficiaires visés</h3>
                  <p className="text-blue-200">selon les objectifs de {stats.beneficiariesProjects} projets</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link to="/projects" className={CTA_PRIMARY}>
                Voir tous les projets
                <IconArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </Suspense>

      {/* Nos Valeurs avec images inspirantes */}
      <Suspense fallback={
        <div className="flex justify-center items-center py-16">
          <div className="animate-pulse bg-gray-200 h-32 w-full max-w-4xl mx-auto rounded-lg"></div>
        </div>
      }>
        <section className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block bg-green-100 text-green-800 text-sm font-semibold px-4 py-2 rounded-full mb-4">
                Nos valeurs
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                L'égalité et les principes de l'économie islamique
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Chacune de nos actions et chaque investissement suivent ces principes, inspirés des enseignements du Coran
                sur le commerce.
              </p>
            </div>

            {/* Grille des valeurs avec images */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {VALUES.map(value => (
                <div key={value.title} className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={value.photo}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${value.tone} via-black/20 to-transparent`} />
                    <div className="absolute bottom-4 left-4 right-4">
                      <value.icon className="h-7 w-7 text-white mb-2" aria-hidden="true" />
                      <h3 className="text-white text-lg font-bold leading-snug">{value.title}</h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600">{value.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-10 text-center">
              <Link to="/investissement-revenus#principes" className="inline-flex items-center gap-2 font-semibold text-green-800 underline">
                Lire nos principes de répartition des revenus
              </Link>
            </p>
          </div>
        </section>
      </Suspense>

      {/* Section Images Parlantes - Notre Impact */}
      <section className="py-20 bg-gradient-to-br from-green-50 via-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
               <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Notre impact en images</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Des activités comme celles que financent nos investisseurs, photographiées au Niger
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Image Communauté */}
            <div className="relative group overflow-hidden rounded-2xl shadow-xl bg-white p-3">
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={PHOTOS.eau}
                  alt="Femmes au puits"
                  className="w-full h-48 object-cover transition-all duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-bold text-sm mb-1"><IconUsers className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden="true" /> Communautés</h3>
                    <p className="text-white/90 text-xs">Impact humain direct</p>
                  </div>
                </div>
              </div>
              <div className="p-4 text-center">
                <h4 className="font-bold text-gray-800 text-sm">Bénéficiaires visés</h4>
                <p className="text-xs text-gray-600 mt-1">{formatCount(stats.beneficiariesTarget)} personnes</p>
              </div>
            </div>

            {/* Image Impact Investment */}
            <div className="relative group overflow-hidden rounded-2xl shadow-xl bg-white p-3">
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={PHOTOS.commerce}
                  alt="Boutique de semences de la région de Maradi"
                  className="w-full h-48 object-cover transition-all duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-bold text-sm mb-1"><IconChartLine className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden="true" /> Croissance</h3>
                    <p className="text-white/90 text-xs">ROI social mesurable</p>
                  </div>
                </div>
              </div>
              <div className="p-4 text-center">
                <h4 className="font-bold text-gray-800 text-sm">Rendement visé</h4>
                <p className="text-xs text-gray-600 mt-1">{formatReturnRange(stats)} par an, non garanti</p>
              </div>
            </div>

            {/* Image Finance */}
            <div className="relative group overflow-hidden rounded-2xl shadow-xl bg-white p-3">
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={financeImage}
                  alt="Gestion financière transparente"
                  className="w-full h-48 object-cover transition-all duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-bold text-sm mb-1"><IconCoins className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden="true" /> Transparence</h3>
                    <p className="text-white/90 text-xs">Gestion rigoureuse</p>
                  </div>
                </div>
              </div>
              <div className="p-4 text-center">
                <h4 className="font-bold text-gray-800 text-sm">Paiements suivis</h4>
                <p className="text-xs text-gray-600 mt-1">Statut visible dans votre portefeuille</p>
              </div>
            </div>

            {/* Image Investment */}
            <div className="relative group overflow-hidden rounded-2xl shadow-xl bg-white p-3">
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={PHOTOS.broyeur}
                  alt="Broyeur d'aliments pour le bétail à Maradi"
                  className="w-full h-48 object-cover transition-all duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white font-bold text-sm mb-1"><IconBullseye className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden="true" /> Opportunités</h3>
                    <p className="text-white/90 text-xs">Investissement intelligent</p>
                  </div>
                </div>
              </div>
              <div className="p-4 text-center">
                <h4 className="font-bold text-gray-800 text-sm">Projets ouverts</h4>
                <p className="text-xs text-gray-600 mt-1">{stats.openProjects} en cours de financement</p>
              </div>
            </div>
          </div>

          <details className="-mt-6 mb-10 text-center text-xs text-gray-500">
            <summary className="cursor-pointer">Crédits des photos d'illustration</summary>
            <ul className="mt-2 space-y-1">
              {SECTORS.filter(sector => PHOTO_SECTORS.includes(sector.label) && sector.credit).map(sector => (
                <li key={sector.label}>
                  {sector.credit.subject} :{' '}
                  <a href={sector.credit.page} target="_blank" rel="noopener noreferrer" className="underline">
                    {sector.credit.author}, Wikimedia Commons
                  </a>, {sector.credit.license}.
                </li>
              ))}
              {defaultProjects.filter(project => PHOTO_PROJECTS.includes(project.id) && project.imageCredit).map(project => (
                <li key={project.id}>{project.imageCredit}</li>
              ))}
            </ul>
          </details>

          {/* Call to Action centré */}
          <div className="text-center">
            <Link to="/impact-social" className={CTA_SECONDARY}>
              Mesurer l'impact de nos projets
              <IconArrowRight aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section Statistiques */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Impact en chiffres
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Calculés automatiquement à partir des projets publiés et des paiements confirmés
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <IconBadge
              iconComponent={IconUsers}
              title="Bénéficiaires visés"
              value={formatCount(stats.beneficiariesTarget)}
              description={`Objectifs déclarés par ${stats.beneficiariesProjects} projets`}
              color="blue"
              variant="floating"
              animated={true}
            />
            <IconBadge
              iconComponent={IconChartLine}
              title="Rendement visé moyen"
              value={formatRate(stats.weightedReturn)}
              description="Par an, pondéré par projet, non garanti"
              color="green"
              variant="floating"
              animated={true}
            />
            <IconBadge
              iconComponent={IconMapMarkerAlt}
              title="Villes couvertes"
              value={formatCount(stats.cities.length)}
              description={stats.cities.map(c => c.name).join(', ') || '—'}
              color="orange"
              variant="floating"
              animated={true}
            />
            {launching ? (
              <IconBadge
                iconComponent={IconBullseye}
                title="Objectif de collecte"
                value={formatXofCompact(stats.totalTarget)}
                description={`Pour ${stats.openProjects} projets ouverts, dès ${MIN_INVESTMENT_LABEL}`}
                color="purple"
                variant="floating"
                animated={true}
              />
            ) : (
              <IconBadge
                iconComponent={IconPercent}
                title="Taux de financement"
                value={`${stats.fundingRate} %`}
                description={`${formatXofCompact(stats.totalRaised)} collectés sur ${formatXofCompact(stats.totalTarget)}`}
                color="purple"
                variant="floating"
                animated={true}
              />
            )}
          </div>
        </div>
      </section>

      {/* Comment ça marche (remplace les témoignages : seuls des retours réels, avec accord, seront publiés) */}
      <section className="py-20 bg-white" aria-labelledby="how-it-works-title">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="how-it-works-title" className="text-3xl font-bold text-gray-900">Comment ça marche</h2>
          <p className="mt-3 max-w-3xl text-lg text-gray-600">Trois étapes pour investir dans un projet près de chez vous.</p>
          <ol className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { title: 'Choisissez un projet', text: 'Agriculture, élevage, commerce, énergie… Chaque projet présente son porteur, son budget et ses objectifs.' },
              { title: 'Investissez', text: `Financez à partir de ${MIN_INVESTMENT_LABEL}, ou achetez des parts pour devenir associé.` },
              { title: 'Suivez votre impact', text: "Dans votre portefeuille, consultez vos contributions, l'avancement du projet, les photos, les rapports et les emplois créés." }
            ].map((step, index) => (
              <li key={step.title} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-blue-700 font-bold text-white">{index + 1}</span>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">{step.title}</h3>
                <p className="mt-2 text-gray-600">{step.text}</p>
              </li>
            ))}
          </ol>
          <p className="mt-6 max-w-3xl rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="note">
            <strong>Un bon investissement commence par une bonne analyse.</strong> Investir comporte un risque de perte en
            capital et d'immobilisation de votre argent ; les rendements visés ne sont pas garantis.
          </p>
          <p className="mt-4 max-w-3xl rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-900" role="note">
            <strong><IconBookOpen className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden="true" /> Un partage fondé sur le Coran.</strong> {SHARING_SUMMARY}{' '}
            <Link to="/investissement-revenus#principes" className="font-semibold underline">Lire les principes</Link>
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link to="/projects" className={CTA_PRIMARY}>Découvrir les projets</Link>
            <Link to="/investissement-revenus#documents" className={CTA_SECONDARY}>Pièces et documents requis</Link>
          </div>
          <p className="mt-6 text-sm text-gray-500">
            Vous êtes entrepreneur ? <Link to="/financement" className="font-semibold text-blue-700 underline">Faites financer votre projet</Link>.
          </p>
        </div>
      </section>

      {/* Qui sommes-nous : une entreprise enregistrée au Niger, joignable, avec un responsable nommé
          (informations de src/config/site.js, les mêmes que les mentions légales) */}
      <section className="bg-gray-50 py-16" aria-labelledby="confiance-titre">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="confiance-titre" className="text-3xl font-bold text-gray-900">Qui gère votre argent ?</h2>
            <p className="mx-auto mt-3 max-w-2xl text-lg text-gray-600">
              Une entreprise enregistrée au Niger, basée à {SITE.city}, que vous pouvez rencontrer et appeler.
            </p>
          </div>
          <dl className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <dt className="flex items-center gap-2 text-sm font-semibold text-gray-500"><IconFileAlt aria-hidden="true" /> Société</dt>
              <dd className="mt-2 font-semibold text-gray-900">{SITE.legalName}</dd>
              <dd className="mt-1 text-sm text-gray-600">{SITE.legalForm}</dd>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <dt className="flex items-center gap-2 text-sm font-semibold text-gray-500"><IconIdCard aria-hidden="true" /> Immatriculation</dt>
              <dd className="mt-2 font-semibold text-gray-900">RCCM {SITE.rccm}</dd>
              <dd className="mt-1 text-sm text-gray-600">du {SITE.rccmDate} · NIF {SITE.nif}</dd>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <dt className="flex items-center gap-2 text-sm font-semibold text-gray-500"><IconUserTie aria-hidden="true" /> Responsable</dt>
              <dd className="mt-2 font-semibold text-gray-900">{SITE.publicationDirector}</dd>
              <dd className="mt-1 text-sm text-gray-600">{SITE.publicationDirectorRole}</dd>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-5">
              <dt className="flex items-center gap-2 text-sm font-semibold text-gray-500"><IconMapMarkerAlt aria-hidden="true" /> Adresse</dt>
              <dd className="mt-2 font-semibold text-gray-900">{SITE.postalAddress}</dd>
              <dd className="mt-1 text-sm text-gray-600"><a href={SITE.phoneHref} className="underline">{SITE.phone}</a></dd>
            </div>
          </dl>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" className={CTA_SECONDARY}>
              <IconWhatsapp aria-hidden="true" /> Poser une question sur WhatsApp
            </a>
            <Link to="/equipe" className="font-semibold text-blue-700 underline">Rencontrer l'équipe</Link>
            <Link to="/mentions-legales" className="font-semibold text-blue-700 underline">Mentions légales</Link>
          </div>
        </div>
      </section>


      {/* Plateforme d'Investissement à Impact avec visuels */}
      <section className='py-20 bg-gradient-to-br from-gray-900 to-blue-900 text-white' aria-labelledby="home-stats-title">
        <div className='max-w-7xl mx-auto px-6'>
          <div className='text-center mb-16'>
            <span className="inline-block bg-blue-100 text-blue-800 text-sm font-semibold px-4 py-2 rounded-full mb-4">
               Notre plateforme
            </span>
            <h2 id='home-stats-title' className='text-3xl sm:text-4xl font-bold mb-6 text-white'>
              Plateforme d'investissement à impact
            </h2>
            <p className='text-xl text-blue-200 max-w-3xl mx-auto'>
              Agriculteurs, éleveurs, commerçantes, jeunes du numérique : financez des entrepreneurs du Niger,
              depuis le pays ou la diaspora, sans intérêt, et suivez ce que devient votre argent
            </p>
          </div>

          {/* Section avec image de dashboard */}
          <div className="mb-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-6">Votre argent suivi pas à pas</h3>
                <p className="text-xl text-blue-200 mb-8">
                  Dans votre portefeuille : chaque paiement confirmé, l'avancement du projet, et les photos et rapports
                  publiés par l'équipe à chaque étape.
                </p>
                <ul className="space-y-4 text-blue-200">
                  <li className="flex items-center">
                    <IconChartLine className="text-green-400 mr-3" />
                    Suivi de chaque investissement et de son statut de paiement
                  </li>
                  <li className="flex items-center">
                    <IconShieldAlt className="text-blue-400 mr-3" />
                    Paiements Mobile Money, BIA CASH et virement, confirmés par l'opérateur
                  </li>
                  <li className="flex items-center">
                    <IconUsers className="text-purple-400 mr-3" />
                    Identité de chaque porteur vérifiée avant qu'il puisse présenter un projet
                  </li>
                </ul>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Interface moderne de la plateforme"
                  className="w-full rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-600/20 to-transparent rounded-2xl" />
              </div>
            </div>
          </div>
          {/* Statistiques avec grid amélioré */}
          <dl className='grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 text-center mb-12'>
            <div className='bg-white/10 backdrop-blur-md p-6 lg:p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300'>
              <div className="mb-4">
                <IconChartLine className="text-4xl text-green-400 mx-auto" />
              </div>
              <dt className='text-blue-200 text-base lg:text-lg mb-2'>{launching ? 'Objectif de collecte' : 'Montant collecté'}</dt>
              <dd className='text-3xl font-bold text-white'><CountUp value={formatXofCompact(launching ? stats.totalTarget : stats.totalRaised)} /></dd>
            </div>
            <div className='bg-white/10 backdrop-blur-md p-6 lg:p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300'>
              <div className="mb-4">
                <IconUsers className="text-4xl text-blue-400 mx-auto" />
              </div>
              <dt className='text-blue-200 text-base lg:text-lg mb-2'>Projets publiés</dt>
              <dd className='text-3xl font-bold text-white'><CountUp value={stats.projectsCount} /></dd>
              <dd className='text-sm text-blue-200 mt-1'>{stats.fundedProjects > 0
                ? `dont ${stats.fundedProjects} financé${stats.fundedProjects > 1 ? 's' : ''} à 100 %`
                : 'tous encore ouverts aux investisseurs'}</dd>
            </div>
            <div className='bg-white/10 backdrop-blur-md p-6 lg:p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300'>
              <div className="mb-4">
                <IconLeaf className="text-4xl text-purple-400 mx-auto" />
              </div>
              <dt className='text-blue-200 text-base lg:text-lg mb-2'>{launching ? 'Pour investir' : 'Investisseurs'}</dt>
              <dd className='text-3xl font-bold text-white'>{launching ? MIN_INVESTMENT_LABEL : <CountUp value={formatCount(stats.investorsCount)} />}</dd>
              {launching && <dd className='text-sm text-blue-200 mt-1'>minimum, par Mobile Money</dd>}
            </div>
            <div className='bg-white/10 backdrop-blur-md p-6 lg:p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300'>
              <div className="mb-4">
                <IconShieldAlt className="text-4xl text-orange-400 mx-auto" />
              </div>
              <dt className='text-blue-200 text-base lg:text-lg mb-2'>Rendement visé moyen</dt>
              <dd className='text-3xl font-bold text-white'><CountUp value={formatRate(stats.weightedReturn)} /></dd>
              <dd className='text-sm text-blue-200 mt-1'>par an, non garanti</dd>
            </div>
          </dl>
          <p className='text-center text-sm text-blue-200 -mt-6 mb-12'>
            Chiffres mis à jour automatiquement à chaque paiement confirmé.
          </p>
          <div className='text-center' aria-labelledby='home-actions-title'>
            <h2 id='home-actions-title' className='sr-only'>Actions rapides</h2>
            <div className='grid grid-cols-2 md:grid-cols-5 gap-4 max-w-6xl mx-auto'>
              {[
                user
                  ? { to: '/profil', icon: IconWallet, title: 'Mon portefeuille', description: 'Suivre mes investissements' }
                  : { to: '/register', icon: IconUserPlus, title: 'Créer un compte', description: 'Gratuit, en 2 minutes' },
                { to: '/investissement', icon: IconHandHoldingUsd, title: 'Investir', description: `Dès ${MIN_INVESTMENT_LABEL}, par Mobile Money` },
                { to: '/financement', icon: IconRocket, title: 'Financer mon projet', description: 'Présenter votre projet aux investisseurs' },
                { to: '/stats', icon: IconTrophy, title: 'Statistiques', description: 'Nos résultats détaillés' },
                { to: '/features', icon: IconStar, title: 'Avantages', description: 'Pourquoi nous choisir' }
              ].map(({ to, icon: Icon, title, description }, index) => (
                <Link
                  key={to}
                  to={to}
                  className={`group flex flex-col items-center p-6 rounded-2xl border text-center transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300 ${
                    index === 0
                      ? 'bg-orange-500 border-orange-400 hover:bg-orange-600'
                      : 'bg-white/10 border-white/20 hover:bg-white/20'
                  } ${index === 4 ? 'col-span-2 md:col-span-1' : ''}`}
                >
                  <Icon className='text-3xl text-white mb-3' aria-hidden='true' />
                  <span className='text-lg font-bold text-white mb-1'>{title}</span>
                  <span className='text-sm text-white/80'>{description}</span>
                </Link>
              ))}
            </div>

            {/* Liens institutionnels (aussi dans le menu « À propos » et le pied de page) */}
            <p className='mt-10 text-blue-200'>
              Vous êtes une institution, une ONG ou une entreprise ?{' '}
              <Link to='/partnership' className='font-semibold text-white underline underline-offset-4 hover:text-orange-300'>
                Devenir partenaire
              </Link>
              <span aria-hidden='true'> · </span>
              Envie de soutenir les jeunes ?{' '}
              <Link to='/faire-un-don' className='font-semibold text-white underline underline-offset-4 hover:text-orange-300'>
                Faire un don
              </Link>
              <span aria-hidden='true'> · </span>
              Envie de nous rejoindre ?{' '}
              <Link to='/recrutement' className='font-semibold text-white underline underline-offset-4 hover:text-orange-300'>
                Voir nos offres d'emploi
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Partenaires : bande de logos, affichée dès qu'une convention est signée (src/data/partners.js) */}
      {PARTNERS.length > 0 && (
        <section aria-labelledby="partenaires-titre" className="bg-gray-50 py-14">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <h2 id="partenaires-titre" className="text-2xl font-bold text-gray-900 sm:text-3xl">Ils nous accompagnent</h2>
              <Link to="/partenaires" className="font-semibold text-blue-700 hover:text-blue-900">Tous nos partenaires →</Link>
            </div>
            <div style={{ '--marquee-bg': '#f9fafb' }}><PartnerMarquee partners={PARTNERS} /></div>
          </div>
        </section>
      )}

      {/* Réseaux sociaux officiels (src/config/site.js) */}
      <SocialFollow />
    </div>
  );
};

export default Home;